import React, { useState } from 'react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Area, AreaChart 
} from 'recharts';
import { Card } from './ui/card';
import { TrendingUp, TrendingDown, Calendar, Euro, Info, X } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * Modal Tooltip Component for Total Cap explanation
 */
const ModalTooltip = ({ isOpen, onClose, title, content }) => {
  if (!isOpen) return null;
  
  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      
      {/* Modal */}
      <div 
        className="relative bg-slate-800 border border-white/20 rounded-xl p-6 max-w-sm w-full shadow-2xl animate-in fade-in-0 zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/10 transition-colors"
          aria-label="Close"
        >
          <X className="h-5 w-5 text-foreground-muted hover:text-white" />
        </button>
        
        {/* Content */}
        {title && (
          <h4 className="font-semibold text-white mb-3 pr-8">{title}</h4>
        )}
        <p className="text-sm text-foreground-muted leading-relaxed">{content}</p>
      </div>
    </div>
  );
};

/**
 * Format date for display
 */
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { 
    day: 'numeric', 
    month: 'short', 
    year: '2-digit' 
  });
};

/**
 * Format date for tooltip
 */
const formatTooltipDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-GB', { 
    day: 'numeric', 
    month: 'long', 
    year: 'numeric' 
  });
};

/**
 * Custom tooltip component
 */
const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-background-paper border border-white/10 rounded-lg p-3 shadow-lg">
        <p className="text-xs text-foreground-muted mb-1">
          {formatTooltipDate(data.effective_date)}
        </p>
        <p className="text-lg font-bold">
          €{data.price?.toFixed(2)}
        </p>
      </div>
    );
  }
  return null;
};

/**
 * Full token price evolution chart
 */
const TokenPriceChart = ({
  priceHistory = [],
  initialPrice,
  latestPrice,
  latestPriceDate,
  priceChangePct = 0,
  tokensOutstanding = 0,
  projectGDV = 0,
  projectStatus = '',
  publishedAt,
  showStats = true,
  height = 250,
  className = ''
}) => {
  const [showTotalCapModal, setShowTotalCapModal] = useState(false);
  
  // Transform data for recharts
  const chartData = priceHistory.map((entry) => ({
    ...entry,
    price: entry.price,
    date: formatDate(entry.effective_date)
  }));

  const isPositive = priceChangePct >= 0;

  // Calculate min/max for Y axis with 10% padding and integer ticks
  const prices = chartData.map(d => d.price).filter(p => p != null);
  const lowestPrice = prices.length > 0 ? Math.min(...prices) : (latestPrice || 0);
  const highestPrice = prices.length > 0 ? Math.max(...prices) : (latestPrice || 0);
  
  // minY = floor(0.90 * lowest), maxY = ceil(1.10 * highest)
  const minY = Math.floor(0.90 * lowestPrice);
  const maxY = Math.ceil(1.10 * highestPrice);

  // Calculate Total Cap (tokens outstanding × latest token price)
  const totalCap = (tokensOutstanding || 0) * (latestPrice || 0);

  return (
    <div className={cn("", className)}>
      {/* Stats Row - 3 tiles in a single row on all devices */}
      {showStats && (
        <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
          {/* Latest Price - Highlighted with inline change */}
          <div className="bg-pink-500/20 border border-pink-500/30 rounded-lg p-2 sm:p-3">
            <div className="flex items-center gap-1 text-[10px] sm:text-xs text-pink-300 mb-0.5 sm:mb-1">
              <Euro className="h-3 w-3 flex-shrink-0" />
              <span className="font-medium truncate">Latest Price</span>
            </div>
            <div className="flex items-baseline gap-1 flex-wrap">
              <span className="text-base sm:text-xl font-bold text-pink-100">
                €{latestPrice?.toFixed(2) || '0.00'}
              </span>
              {priceChangePct !== 0 && priceChangePct != null && (
                <span className={cn(
                  "text-[10px] sm:text-xs font-medium",
                  isPositive ? "text-emerald-400" : "text-red-400"
                )}>
                  ({isPositive ? '+' : ''}{priceChangePct?.toFixed(1)}%)
                </span>
              )}
            </div>
          </div>
          
          {/* Initial Price */}
          <div className="bg-white/5 rounded-lg p-2 sm:p-3">
            <div className="flex items-center gap-1 text-[10px] sm:text-xs text-foreground-muted mb-0.5 sm:mb-1">
              <Euro className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">Initial Price</span>
            </div>
            <div className="text-base sm:text-lg font-bold">€{initialPrice?.toFixed(2) || '0.00'}</div>
          </div>
          
          {/* Total Cap with modal tooltip */}
          <div className="bg-white/5 rounded-lg p-2 sm:p-3">
            <div className="flex items-center gap-1 text-[10px] sm:text-xs text-foreground-muted mb-0.5 sm:mb-1">
              <Euro className="h-3 w-3 flex-shrink-0" />
              <span className="truncate">Total Cap</span>
              <button
                onClick={() => setShowTotalCapModal(true)}
                className="ml-auto p-0.5 rounded-full hover:bg-white/10 transition-colors flex-shrink-0"
                aria-label="What is Total Cap?"
              >
                <Info className="h-3 w-3 text-foreground-muted hover:text-primary" />
              </button>
            </div>
            <div className="text-base sm:text-lg font-bold">€{totalCap?.toLocaleString() || '0'}</div>
          </div>
        </div>
      )}

      {/* Total Cap Modal */}
      <ModalTooltip
        isOpen={showTotalCapModal}
        onClose={() => setShowTotalCapModal(false)}
        title="Total Cap"
        content="Total Cap represents the gross development value (GDV) of the project, calculated as the token market value × total tokens outstanding."
      />

      {/* Chart */}
      {chartData.length > 0 ? (
        <div style={{ height }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop 
                    offset="5%" 
                    stopColor={isPositive ? "#10b981" : "#ef4444"} 
                    stopOpacity={0.3}
                  />
                  <stop 
                    offset="95%" 
                    stopColor={isPositive ? "#10b981" : "#ef4444"} 
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <CartesianGrid 
                strokeDasharray="3 3" 
                vertical={false}
                stroke="rgba(255,255,255,0.05)"
              />
              <XAxis 
                dataKey="date" 
                axisLine={false}
                tickLine={false}
                tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }}
                dy={10}
              />
              <YAxis 
                domain={[minY, maxY]}
                axisLine={false}
                tickLine={false}
                tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }}
                tickFormatter={(value) => `€${Math.round(value)}`}
                width={50}
                allowDecimals={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="price"
                stroke={isPositive ? "#10b981" : "#ef4444"}
                strokeWidth={2}
                fill="url(#priceGradient)"
                dot={chartData.length <= 20}
                activeDot={{ r: 4, fill: isPositive ? "#10b981" : "#ef4444" }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div 
          className="flex items-center justify-center text-foreground-muted"
          style={{ height }}
        >
          <p className="text-sm">No price history available</p>
        </div>
      )}

      {/* Footer info */}
      {chartData.length > 0 && (
        <div className="flex items-center justify-between mt-3 text-xs text-foreground-muted">
          <span>
            <Calendar className="h-3 w-3 inline mr-1" />
            Published: {formatDate(publishedAt)}
          </span>
          <span>
            {chartData.length} price point{chartData.length !== 1 ? 's' : ''}
          </span>
          {latestPriceDate && (
            <span>Last update: {formatDate(latestPriceDate)}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default TokenPriceChart;
